if (!customElements.get('sam-film-player')) {
  customElements.define('sam-film-player', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.video = this.querySelector('video');
      this.trigger = this.querySelector('[data-film-open]');
      if (!this.dialog?.showModal || !this.video || !this.trigger) return;
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      const on = (target, event, handler) => target.addEventListener(event, handler, options);
      this.frame = this.querySelector('.sam-film-dialog__frame');
      this.progress = this.querySelector('[data-film-progress]');
      this.volume = this.querySelector('[data-film-volume]');
      this.mute = this.querySelector('[data-film-mute]');
      this.fullscreen = this.querySelector('[data-film-fullscreen]');
      this.status = this.querySelector('[role="status"]');
      this.elapsed = this.querySelector('[data-film-elapsed]');
      this.duration = this.querySelector('[data-film-duration]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.lastVolume = 1;
      on(this.trigger, 'click', (event) => { event.preventDefault(); this.open(); });
      on(this.querySelector('[data-film-close]'), 'click', () => this.close());
      on(this.dialog, 'cancel', (event) => { event.preventDefault(); this.close(); });
      on(this.dialog, 'pointerdown', (event) => { this.backdropPressed = event.target === this.dialog; });
      on(this.dialog, 'click', (event) => {
        if (event.target === this.dialog && this.backdropPressed) this.close();
      });
      this.querySelectorAll('[data-film-toggle]').forEach(button => on(button, 'click', () => this.toggle()));
      on(this.video, 'click', () => {
        if (matchMedia('(pointer: coarse)').matches && !this.dialog.classList.contains('controls-visible')) {
          this.showControls();
        } else this.toggle();
      });
      on(this.frame, 'pointermove', () => this.showControls());
      on(this.frame, 'pointerdown', () => { this.interacting = true; this.showControls(); });
      on(window, 'pointerup', () => { this.interacting = false; if (this.dialog.open) this.showControls(); });
      on(window, 'pointercancel', () => { this.interacting = false; if (this.dialog.open) this.showControls(); });
      on(this.frame, 'focusin', () => this.showControls());
      on(this.frame, 'focusout', () => this.showControls());
      on(this.dialog, 'keydown', (event) => {
        if (event.key === ' ' && !event.target.closest('button, input, a')) {
          event.preventDefault(); this.toggle();
        }
      });
      ['play', 'pause', 'ended'].forEach(event => on(this.video, event, () => this.syncPlayback()));
      ['timeupdate', 'loadedmetadata', 'durationchange'].forEach(event => on(this.video, event, () => this.syncTime()));
      on(this.video, 'error', () => {
        if (this.dialog.open) this.message('The film couldn’t load. Close it and try again.');
      });
      on(this.progress, 'input', () => {
        if (Number.isFinite(this.video.duration)) {
          this.video.currentTime = Number(this.progress.value);
          this.syncTime();
          this.showControls();
        }
      });
      on(this.mute, 'click', () => {
        if (this.video.muted || this.video.volume === 0) {
          this.video.muted = false;
          if (this.video.volume === 0) this.video.volume = this.lastVolume || 1;
        } else this.video.muted = true;
        this.syncVolume();
      });
      on(this.volume, 'input', () => {
        const volume = Number(this.volume.value);
        this.video.volume = volume;
        this.video.muted = volume === 0;
        if (volume > 0) this.lastVolume = volume;
        this.syncVolume();
      });
      on(this.video, 'volumechange', () => this.syncVolume());
      on(this.fullscreen, 'click', () => this.toggleFullscreen());
      on(document, 'fullscreenchange', () => this.syncFullscreen());
      on(document, 'webkitfullscreenchange', () => this.syncFullscreen());
      on(this.video, 'webkitbeginfullscreen', () => this.syncFullscreen(true));
      on(this.video, 'webkitendfullscreen', () => this.syncFullscreen(false));
      // iOS reserves playback volume for the device's hardware controls.
      const deviceVolume = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (deviceVolume) {
        this.volume.hidden = true;
        this.mute.title = 'Mute or use your device buttons to adjust volume';
      }
      this.syncVolume();
      this.syncTime();
    }

    open() {
      if (this.dialog.open) return;
      clearTimeout(this.closeTimer);
      this.closing = false;
      this.returnFocus = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.scrollLocked = true;
      this.dialog.showModal();
      this.dialog.focus({ preventScroll: true });
      this.dialog.getBoundingClientRect();
      this.status.hidden = true;
      this.dialog.classList.add('controls-visible');
      if (!this.loaded || this.video.error) {
        this.video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
        this.video.load();
        this.loaded = true;
      }
      this.openFrame = requestAnimationFrame(() => {
        this.dialog.classList.add('is-open');
      });
      // Request sound playback inside the click gesture, including on iOS.
      // The frame reveals it during the same 400ms spring animation.
      this.play();
    }

    async play() {
      if (!this.dialog.open || this.closing) return;
      this.status.hidden = true;
      if (this.video.ended) this.video.currentTime = 0;
      try {
        await this.video.play();
        if (!this.dialog.open || this.closing) this.video.pause();
      } catch (error) {
        if (this.dialog.open && !this.closing && error.name !== 'AbortError') {
          this.message('Press play to start the film.');
          this.syncPlayback();
        }
      }
    }

    toggle() {
      if (this.video.paused || this.video.ended) this.play();
      else this.video.pause();
    }

    close(immediate = false) {
      if (!this.dialog?.open || this.closing) return;
      this.closing = true;
      cancelAnimationFrame(this.openFrame);
      clearTimeout(this.hideTimer);
      this.video.pause();
      this.dialog.classList.remove('is-open');
      if (document.fullscreenElement === this.frame) document.exitFullscreen().catch(() => {});
      else if (document.webkitFullscreenElement === this.frame) document.webkitExitFullscreen?.();
      const finish = () => {
        this.video.currentTime = 0;
        this.dialog.close();
        this.restoreScroll();
        this.closing = false;
        if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      };
      if (immediate || this.motion.matches) finish();
      else this.closeTimer = setTimeout(finish, 400);
    }

    restoreScroll() {
      if (this.scrollLocked) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.scrollLocked = false;
      }
    }

    syncPlayback() {
      const playing = !this.video.paused && !this.video.ended;
      this.querySelectorAll('[data-film-toggle]').forEach(button => {
        button.setAttribute('aria-label', playing ? 'Pause film' : this.video.ended ? 'Replay film' : 'Play film');
        button.querySelector('[data-play-icon]').hidden = playing;
        button.querySelector('[data-pause-icon]').hidden = !playing;
      });
      this.showControls();
    }

    showControls() {
      if (!this.dialog.open || this.closing) return;
      clearTimeout(this.hideTimer);
      this.dialog.classList.add('controls-visible');
      if (this.video.paused || this.video.ended) return;
      this.hideTimer = setTimeout(() => {
        const focused = document.activeElement;
        const keyboardControl = focused?.matches(':focus-visible') && focused.closest('.sam-film-dialog__controls, .sam-film-dialog__center');
        if (!this.interacting && !keyboardControl && !this.video.paused) this.dialog.classList.remove('controls-visible');
      }, 800);
    }

    syncTime() {
      const duration = Number.isFinite(this.video.duration) ? this.video.duration : 0;
      const current = this.video.currentTime || 0;
      this.progress.disabled = duration <= 0;
      this.progress.max = duration || 100;
      this.progress.value = current;
      this.progress.style.setProperty('--range-fill', `${duration ? current / duration * 100 : 0}%`);
      this.progress.setAttribute('aria-valuetext', `${this.time(current)} of ${this.time(duration)}`);
      this.elapsed.textContent = this.time(current);
      this.duration.textContent = this.time(duration);
    }

    time(seconds) {
      return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
    }

    syncVolume() {
      const muted = this.video.muted || this.video.volume === 0;
      const value = muted ? 0 : this.video.volume;
      this.mute.classList.toggle('is-muted', muted);
      this.mute.setAttribute('aria-pressed', String(muted));
      this.mute.setAttribute('aria-label', muted ? 'Unmute film' : 'Mute film');
      this.volume.value = value;
      this.volume.style.setProperty('--range-fill', `${value * 100}%`);
      this.volume.setAttribute('aria-valuetext', `${Math.round(value * 100)}%`);
    }

    async toggleFullscreen() {
      try {
        if (document.fullscreenElement) await document.exitFullscreen();
        else if (document.webkitFullscreenElement) document.webkitExitFullscreen();
        else if (this.frame.requestFullscreen) await this.frame.requestFullscreen();
        else if (this.frame.webkitRequestFullscreen) this.frame.webkitRequestFullscreen();
        else if (this.video.webkitEnterFullscreen) this.video.webkitEnterFullscreen();
        else this.message('Fullscreen isn’t available in this browser.');
      } catch {
        if (this.video.webkitEnterFullscreen) {
          try { this.video.webkitEnterFullscreen(); } catch { this.message('Fullscreen isn’t available in this browser.'); }
        } else this.message('Fullscreen isn’t available in this browser.');
      }
    }

    syncFullscreen(nativeFullscreen = false) {
      const active = document.fullscreenElement === this.frame || document.webkitFullscreenElement === this.frame || nativeFullscreen;
      this.fullscreen.setAttribute('aria-label', active ? 'Exit fullscreen' : 'Enter fullscreen');
      this.fullscreen.setAttribute('aria-pressed', String(active));
      this.showControls();
    }

    message(text) {
      this.status.textContent = text;
      this.status.hidden = false;
      this.showControls();
    }

    disconnectedCallback() {
      clearTimeout(this.hideTimer);
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.openFrame);
      this.video?.pause();
      this.dialog?.close();
      this.restoreScroll();
      this.abort?.abort();
    }
  });
}
